
import { useState } from "react";
import { Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { sanitizeInput, validateEmail } from "@/utils/sanitization";
import { trackNewsletterSubscription, trackExitIntent } from "@/utils/metaPixel";

export const SubscriptionForm = () => {
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email) {
      toast({
        title: "Email Required",
        description: "Please enter your email address.",
        variant: "destructive",
      });
      return;
    }

    if (!validateEmail(email)) {
      toast({
        title: "Invalid Email",
        description: "Please enter a valid email address.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const sanitizedEmail = sanitizeInput(email);
      
      const { error: dbError } = await supabase
        .from("email_subscribers")
        .insert({
          email: sanitizedEmail,
          source: "exit_intent_popup",
          page_url: window.location.pathname,
          subscription_type: ["newsletter", "maintenance_tips"],
        });

      if (dbError) {
        if (dbError.code === "23505") {
          toast({
            title: "Already Subscribed",
            description: "This email is already receiving our updates!",
          });
        } else {
          throw dbError;
        }
      } else {
        const { error: emailError } = await supabase.functions.invoke('send-email', {
          body: {
            type: 'contact',
            data: {
              name: 'New Subscriber',
              email: sanitizedEmail,
              subject: 'New Newsletter Subscription',
              message: `A new user has subscribed to the newsletter:\n\nEmail: ${sanitizedEmail}\nSource: Exit Intent Popup\nPage: ${window.location.pathname}\nSubscription Type: Newsletter, Maintenance Tips`
            }
          }
        });

        if (emailError) {
          console.error('Notification email error:', emailError);
        }

        // Track newsletter subscription and exit intent subscription
        trackNewsletterSubscription();
        trackExitIntent('subscribed');

        toast({
          title: "Success!",
          description: "Your maintenance guide is on its way!",
        });
      }
    } catch (error) {
      console.error("Subscription error:", error);
      toast({
        title: "Subscription Failed",
        description: "Please try again later.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-3">
        <div className="relative">
          <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
          <Input
            type="email"
            placeholder="Enter your email"
            className="pl-9 w-full"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={isSubmitting}
          />
        </div>
        <Button type="submit" className="w-full" disabled={isSubmitting}>
          {isSubmitting ? "Sending..." : "Get It Free"}
        </Button>
      </div>
      <p className="text-xs text-muted-foreground text-center">
        No spam, unsubscribe anytime
      </p>
    </form>
  );
};
